import { Flex, Text } from '@chakra-ui/react'
import {
  format
} from 'date-fns'
import i18n from 'i18next'
import { isNil, startCase } from 'lodash'

import {
  monthDayDateFormat,
  scheduleMaxCalendarDurationInDays,
  weekDayDateFormat
} from '@/constants'
import { getDateFnsLocale } from '@/i18n/utils/date-fns'
import { BackendErrorData, IScheduleTemplateOption } from '@/types'

export function secondsToTime (seconds: number) {
  const m = Math.floor((seconds % 3600) / 60)
    .toString()
    .padStart(2, '0')
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, '0')

  return m + ':' + s
}

export function localizedFormat (date: Date | number, formatPattern: string) {
  const locale = getDateFnsLocale(i18n.resolvedLanguage)

  return format(date, formatPattern, { locale })
}

export function localizedDaysOfTheWeek () {
  const locale = getDateFnsLocale(i18n.resolvedLanguage)

  if (!locale || !locale.localize) return []

  const weekdays = [1, 2, 3, 4, 5, 6, 0].map(i => locale.localize?.day(i))

  return weekdays
}

export function getFormattedDay (date: string) {
  const dateObj = new Date(date).setHours(0, 0, 0, 0)
  const weeklyDay = localizedFormat(dateObj, 'EEEE')
  const monthDay = localizedFormat(dateObj, 'dd MMMM')

  return { weeklyDay, monthDay }
}

export function formatTooltipLabel (val: string) {
  if (isNil(val) || val === '') return ''

  return startCase(
    localizedFormat(new Date(val).setHours(0, 0, 0, 0), weekDayDateFormat)
  )
}

export function formatTick (val: string) {
  if (isNil(val) || val === '') return ''

  return startCase(
    localizedFormat(new Date(val).setHours(0, 0, 0, 0), monthDayDateFormat)
  )
}

export const getDurationOptions = () => [
  {
    label: `1 ${i18n.t('gui.general.week')} (7 ${i18n.t(
      'gui.general.day_other'
    )})`,
    value: 7
  },
  {
    label: `2 ${i18n.t('gui.general.weeks')} (14 ${i18n.t(
      'gui.general.day_other'
    )})`,
    value: 14
  },
  {
    label: `3 ${i18n.t('gui.general.weeks')} (21 ${i18n.t(
      'gui.general.day_other'
    )})`,
    value: 21
  },
  {
    label: `1 ${i18n.t('gui.general.month')} (30 ${i18n.t(
      'gui.general.day_other'
    )})`,
    value: 30
  },
  {
    label: `2 ${i18n.t('gui.general.months')} (60 ${i18n.t(
      'gui.general.day_other'
    )})`,
    value: 60
  },
  {
    label: `3 ${i18n.t('gui.general.months')} (90 ${i18n.t(
      'gui.general.day_other'
    )})`,
    value: 90
  },
  {
    label: `6 ${i18n.t('gui.general.months')} (180 ${i18n.t(
      'gui.general.day_other'
    )})`,
    value: 180
  },
  {
    label: `1 ${i18n.t('gui.general.year')} (${scheduleMaxCalendarDurationInDays} ${i18n.t(
      'gui.general.day_other'
    )})`,
    value: scheduleMaxCalendarDurationInDays
  }
]

export function scheduleTemplatesCustomOption (option: IScheduleTemplateOption) {
  const { label, bodyParts } = option

  const trBodyParts = bodyParts.split(', ')
  const bpList = []
  trBodyParts.forEach(bp => {
    if (bp) bpList.push(i18n.t(`body_parts.${bp}`))
  })
  const stringBodyParts = trBodyParts.join(', ')

  return (
    <Flex flexWrap='wrap' flexDirection='column'>
      <Text color='#4e4e4e' size='md' lineHeight='24px'>
        {stringBodyParts}
      </Text>
      <Text lineHeight='18px' size='sm'>
        {label}
      </Text>
    </Flex>
  )
}

export function getBodyParts () {
  return [
    {
      value: 'all',
      label: i18n.t('gui.general.all')
    },
    {
      value: 'lower_back',
      label: '',
      style: { top: '125px', left: '189px' }
    },
    {
      value: 'shoulder',
      label: '',
      style: { top: '65px', left: '-33px' }
    }
  ]
}

export function generateErrorMessage (err: BackendErrorData) {
  let errMessages = ''

  err.forEach(item => {
    const errField = i18n.t(`backend_messages.fields.${item.incorrectValue}`)
    const errInfo = i18n.t(`backend_messages.${item.details}`)

    errMessages += `${errField}: ${errInfo}\n`
  })

  return errMessages
}
